/**
 * GWP (Gift With Purchase) Rebuy Smart Cart Integration
 *
 * This script handles:
 * 1. Widget visibility based on cart state (show/hide GWP carousel)
 * 2. Graceful GWP removal when cart becomes ineligible
 * 3. GWP recovery for Rebuy one-time/subscription switch flows
 *
 * Events dispatched:
 * - gwp:cart-updated: Fired when cart changes, includes hasGwp boolean
 */

(function () {
  let cartChangeDebounceTimer = null;
  const DEBOUNCE_DELAY = 300;
  const INELIGIBLE_REMOVAL_GRACE_MS = 1500;
  const SWITCH_RECOVERY_DELAY_MS = 900;
  const SWITCH_RECOVERY_WINDOW_MS = 5000;

  const GWP_FLAG_KEY = "_gwp_uqora";
  const GWP_CAMPAIGN_KEY = "_gwp_campaign";
  const GWP_CLAIMED_STORAGE_KEY = "uqora_gwp_claimed";
  const GWP_LAST_VARIANT_STORAGE_KEY = "uqora_gwp_last_variant_id";
  const GWP_LAST_CAMPAIGN_STORAGE_KEY = "uqora_gwp_last_campaign";
  const GWP_INELIGIBLE_PENDING_CLASS = "gwp-ineligible-removal-pending";
  const GWP_NORMALIZATION_PENDING_CLASS = "gwp-normalization-pending";
  const GWP_FREE_PRICE_CONFIRMED_CLASS = "gwp-free-price-confirmed";
  const EXCLUDED_HANDLE_FAMILIES = [
    "wipes",
    "wash",
    "cleansing-wipes",
    "emergency-kit",
    "uti-emergency-kit",
  ];

  let isProcessingGwpRemoval = false;
  let isProcessingGwpRecovery = false;
  let ineligibleRemovalTimer = null;
  let lastSwitchEventAt = 0;
  let lastKnownCartHadGwp = false;
  let isProcessingGwpNormalization = false;

  function isGwpItem(item) {
    return item.properties && item.properties[GWP_FLAG_KEY] === "true";
  }

  function getGwpCampaign(item) {
    return item.properties?.[GWP_CAMPAIGN_KEY] || null;
  }

  function isExcludedHandle(handle) {
    const normalizedHandle = String(handle || "").toLowerCase();
    return EXCLUDED_HANDLE_FAMILIES.some(
      (family) => normalizedHandle === family || normalizedHandle.startsWith(`${family}-`)
    );
  }

  function getTotalGwpQuantity(cartItems) {
    return cartItems
      .filter(isGwpItem)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  function setGwpPriceVisualState(cartItems) {
    if (!document.body) return;
    const gwpItems = cartItems.filter(isGwpItem);
    const hasOneFreeGift =
      getTotalGwpQuantity(cartItems) === 1 &&
      gwpItems.length === 1 &&
      Number(gwpItems[0].final_line_price ?? gwpItems[0].final_price) === 0;
    document.body.classList.toggle(GWP_FREE_PRICE_CONFIRMED_CLASS, hasOneFreeGift);
  }

  function hasEligibleParentItems(cartItems, campaign = null) {
    const activeOffer = campaign ? window.UqoraCampaignOffers?.getActiveOffer?.() : null;
    if (campaign && activeOffer?.key !== campaign) return false;

    return cartItems.some(
      (item) => !isGwpItem(item) && !isExcludedHandle(item.handle)
    );
  }

  function getIneligibleGwpItems(cartItems) {
    return cartItems.filter(
      (item) => isGwpItem(item) && !hasEligibleParentItems(cartItems, getGwpCampaign(item))
    );
  }

  async function fetchCart() {
    const cartResponse = await fetch("/cart.js");
    if (!cartResponse.ok) {
      throw new Error(`cart read failed: ${cartResponse.status}`);
    }
    return cartResponse.json();
  }

  function rememberLastGwpVariantId(cartItems) {
    const gwpItem = cartItems.find(isGwpItem);
    if (!gwpItem || !gwpItem.variant_id) return;

    try {
      localStorage.setItem(GWP_LAST_VARIANT_STORAGE_KEY, String(gwpItem.variant_id));
      const campaign = getGwpCampaign(gwpItem);
      if (campaign) {
        localStorage.setItem(GWP_LAST_CAMPAIGN_STORAGE_KEY, campaign);
      } else {
        localStorage.removeItem(GWP_LAST_CAMPAIGN_STORAGE_KEY);
      }
    } catch (e) {
      // localStorage not available
    }
  }

  function getRememberedGwpVariantId() {
    try {
      const value = localStorage.getItem(GWP_LAST_VARIANT_STORAGE_KEY);
      if (!value) return null;
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function getRememberedGwpCampaign() {
    try {
      return localStorage.getItem(GWP_LAST_CAMPAIGN_STORAGE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function clearClaimedStorage() {
    try {
      localStorage.removeItem(GWP_CLAIMED_STORAGE_KEY);
      const rememberedCampaign = getRememberedGwpCampaign();
      if (rememberedCampaign) {
        localStorage.removeItem(`${GWP_CLAIMED_STORAGE_KEY}_${rememberedCampaign}`);
      }
    } catch (e) {
      // localStorage not available
    }
  }

  function setIneligiblePendingVisualState(enabled) {
    if (!document.body) return;
    document.body.classList.toggle(GWP_INELIGIBLE_PENDING_CLASS, Boolean(enabled));
  }

  /**
   * Checks if cart contains a GWP item and dispatches visibility event.
   * @param {Array} cartItems - Current cart items
   */
  function checkAndDispatchGwpVisibility(cartItems) {
    const hasGwp = cartItems.some(isGwpItem);
    lastKnownCartHadGwp = hasGwp;
    setGwpPriceVisualState(cartItems);

    if (hasGwp) {
      rememberLastGwpVariantId(cartItems);
    }

    document.dispatchEvent(
      new CustomEvent("gwp:cart-updated", {
        detail: { hasGwp, itemCount: cartItems.length },
      })
    );

    // Also dispatch legacy events for backwards compatibility
    if (hasGwp) {
      document.dispatchEvent(new CustomEvent("gwp:claimed"));
    }
  }

  async function removeGwpItems(gwpKeys) {
    if (!gwpKeys.length) return;

    isProcessingGwpRemoval = true;
    setIneligiblePendingVisualState(true);
    try {
      const updates = {};
      gwpKeys.forEach((key) => {
        updates[key] = 0;
      });

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        throw new Error(`cart GWP removal failed: ${response.status}`);
      }

      clearClaimedStorage();

      const updatedCart = await fetchCart();
      checkAndDispatchGwpVisibility(updatedCart.items);
    } catch (error) {
      console.error("GWP: Error removing ineligible gift items:", error);
    } finally {
      setIneligiblePendingVisualState(false);
      setTimeout(() => {
        isProcessingGwpRemoval = false;
      }, 500);
    }
  }

  async function normalizeGwpItems(cartItems) {
    const gwpItems = cartItems.filter(isGwpItem);
    if (getTotalGwpQuantity(cartItems) <= 1) return cartItems;
    if (isProcessingGwpNormalization) return cartItems;

    isProcessingGwpNormalization = true;
    document.body?.classList.add(GWP_NORMALIZATION_PENDING_CLASS);
    try {
      const eligibleKeeper = gwpItems.find((item) =>
        hasEligibleParentItems(cartItems, getGwpCampaign(item))
      );
      const keeper = eligibleKeeper || gwpItems[0];
      const updates = {};
      gwpItems.forEach((item) => {
        updates[item.key] = item.key === keeper.key ? 1 : 0;
      });

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) {
        throw new Error(`cart GWP normalization failed: ${response.status}`);
      }
      const updatedCart = await response.json();
      checkAndDispatchGwpVisibility(updatedCart.items);
      return updatedCart.items;
    } catch (error) {
      console.error("GWP: Error normalizing duplicate gift items:", error);
      setGwpPriceVisualState(cartItems);
      return cartItems;
    } finally {
      document.body?.classList.remove(GWP_NORMALIZATION_PENDING_CLASS);
      isProcessingGwpNormalization = false;
    }
  }

  function scheduleIneligibleGwpRemoval(cartItems) {
    if (ineligibleRemovalTimer) {
      clearTimeout(ineligibleRemovalTimer);
      ineligibleRemovalTimer = null;
    }

    const gwpItems = cartItems.filter(isGwpItem);
    if (!gwpItems.length) {
      setIneligiblePendingVisualState(false);
      return;
    }
    const ineligibleGwpItems = getIneligibleGwpItems(cartItems);
    if (!ineligibleGwpItems.length) {
      setIneligiblePendingVisualState(false);
      return;
    }

    setIneligiblePendingVisualState(true);

    ineligibleRemovalTimer = setTimeout(async () => {
      if (isProcessingGwpRemoval || isProcessingGwpRecovery) return;

      try {
        const latestCart = await fetchCart();
        const latestGwpItems = latestCart.items.filter(isGwpItem);
        const latestIneligibleGwpItems = getIneligibleGwpItems(latestCart.items);

        if (!latestGwpItems.length || !latestIneligibleGwpItems.length) {
          setIneligiblePendingVisualState(false);
          return;
        }

        await removeGwpItems(latestIneligibleGwpItems.map((item) => item.key));
      } catch (error) {
        console.error("GWP: Error confirming ineligible removal:", error);
      } finally {
        ineligibleRemovalTimer = null;
      }
    }, INELIGIBLE_REMOVAL_GRACE_MS);
  }

  function hasActiveSwitchRecoveryWindow() {
    return Date.now() - lastSwitchEventAt <= SWITCH_RECOVERY_WINDOW_MS;
  }

  async function attemptSwitchGiftRecovery() {
    if (isProcessingGwpRecovery || isProcessingGwpRemoval) return;
    if (!hasActiveSwitchRecoveryWindow()) return;

    const recoveredVariantId = getRememberedGwpVariantId();
    if (!recoveredVariantId) return;
    const recoveredCampaign = getRememberedGwpCampaign();
    const activeOffer = recoveredCampaign
      ? window.UqoraCampaignOffers?.getActiveOffer?.()
      : null;
    if (
      recoveredCampaign &&
      (activeOffer?.key !== recoveredCampaign || activeOffer.giftVariantId !== recoveredVariantId)
    ) {
      return;
    }

    try {
      if (recoveredCampaign) {
        const campaignReady = await window.UqoraCampaignOffers.ensureCartAttribute();
        if (!campaignReady) return;
      }

      const cart = await fetchCart();
      const hasGwp = cart.items.some(isGwpItem);
      const eligibleForGwp = hasEligibleParentItems(cart.items, recoveredCampaign);

      if (hasGwp || !eligibleForGwp) {
        setIneligiblePendingVisualState(false);
        return;
      }

      isProcessingGwpRecovery = true;

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: recoveredVariantId,
              quantity: 1,
              properties: {
                _gwp_uqora: "true",
                ...(recoveredCampaign && {
                  _gwp_campaign: recoveredCampaign,
                }),
              },
            },
          ],
        }),
      });
      if (!response.ok) {
        throw new Error(`cart GWP recovery failed: ${response.status}`);
      }

      const updatedCart = await fetchCart();
      checkAndDispatchGwpVisibility(updatedCart.items);
    } catch (error) {
      console.error("GWP: Error recovering gift after Rebuy switch:", error);
    } finally {
      setIneligiblePendingVisualState(false);
      isProcessingGwpRecovery = false;
    }
  }

  function markSwitchEventAndScheduleRecovery() {
    if (!lastKnownCartHadGwp) return;

    lastSwitchEventAt = Date.now();
    setTimeout(() => {
      attemptSwitchGiftRecovery();
    }, SWITCH_RECOVERY_DELAY_MS);
  }

  // Listen for Smart Cart item removal
  // NOTE: We don't remove GWP immediately on this event. We evaluate eligibility
  // after cart settles to avoid false positives during switch flows.
  document.addEventListener('rebuy:smartcart.line-item-removed', (event) => {
    const removedItem = event.detail.item;
    console.log('GWP: Item removed from Smart Cart:', removedItem.key, removedItem.properties);
  });

  document.addEventListener("rebuy:smartcart.item-switch-to-subscription", () => {
    markSwitchEventAndScheduleRecovery();
  });

  document.addEventListener("rebuy:smartcart.item-switch-to-one-time", () => {
    markSwitchEventAndScheduleRecovery();
  });

  // Listen for Smart Cart open to check visibility
  document.addEventListener('rebuy:smartcart.show', async () => {
    try {
      const cart = await fetchCart();
      const reconciledItems = await normalizeGwpItems(cart.items);
      checkAndDispatchGwpVisibility(reconciledItems);
    } catch (error) {
      console.error('GWP: Error checking cart on Smart Cart show:', error);
    }
  });

  // Listen for items added to cart via Rebuy
  document.addEventListener('rebuy:cart.add', async () => {
    try {
      const cart = await fetchCart();
      const reconciledItems = await normalizeGwpItems(cart.items);
      checkAndDispatchGwpVisibility(reconciledItems);
    } catch (error) {
      console.error('GWP: Error checking cart after add:', error);
    }
  });

  // Also listen for cart change events to handle edge cases
  document.addEventListener('rebuy:cart.change', async (event) => {
    if (isProcessingGwpRemoval || isProcessingGwpRecovery) return;

    // Debounce to prevent rapid-fire API calls
    if (cartChangeDebounceTimer) {
      clearTimeout(cartChangeDebounceTimer);
    }

    cartChangeDebounceTimer = setTimeout(async () => {
      try {
        const cart = await fetchCart();

        const reconciledItems = await normalizeGwpItems(cart.items);

        // Dispatch visibility event
        checkAndDispatchGwpVisibility(reconciledItems);

        // Remove gifts only if cart stays ineligible after a grace period.
        scheduleIneligibleGwpRemoval(reconciledItems);

        // During a switch flow, restore remembered GWP if swap finished without one.
        if (hasActiveSwitchRecoveryWindow()) {
          await attemptSwitchGiftRecovery();
        }
      } catch (error) {
        console.error('GWP: Error handling cart change:', error);
        isProcessingGwpRemoval = false;
        isProcessingGwpRecovery = false;
      }
    }, DEBOUNCE_DELAY);
  });

  // Initial check on page load
  (async function initialCheck() {
    try {
      const cart = await fetchCart();
      const reconciledItems = await normalizeGwpItems(cart.items);
      checkAndDispatchGwpVisibility(reconciledItems);
      scheduleIneligibleGwpRemoval(reconciledItems);
    } catch (error) {
      console.error('GWP: Error on initial cart check:', error);
      setIneligiblePendingVisualState(false);
    }
  })();
})();
